/**
 * Enhanced Search Service with Advanced Features
 * Builds upon the existing SearchService with additional capabilities
 */

import {
  SearchService,
  SearchResult,
  SearchResponse,
  SearchOptions,
} from "./searchService";
import { supabase } from "../lib/supabase";

// Enhanced interfaces
export interface EnhancedSearchOptions extends SearchOptions {
  dateRange?: {
    from: Date;
    to: Date;
  };
  categories?: string[];
  tags?: string[];
  author?: string;
  exactMatch?: boolean;
  fuzzySearch?: boolean;
  includeDrafts?: boolean;
  minScore?: number;
}

export interface SearchFacets {
  categories: { [key: string]: number };
  types: { [key: string]: number };
  authors: { [key: string]: number };
  dateRanges: { [key: string]: number };
  tags: { [key: string]: number };
}

export interface EnhancedSearchResponse extends SearchResponse {
  facets: SearchFacets;
  suggestions: string[];
  relatedSearches: string[];
  searchTime: number;
  totalPages: number;
  currentPage: number;
}

export interface SearchAnalytics {
  query: string;
  resultsCount: number;
  searchTime: number;
  userAgent: string;
  clickedResults: string[];
  searchSession: string;
  timestamp: Date;
  filters: Record<string, any>;
}

export interface SearchHistory {
  query: string;
  timestamp: Date;
  resultCount: number;
  clickedResults: string[];
  filters: Record<string, any>;
}

export interface ParsedQuery {
  original: string;
  cleanQuery: string;
  exactPhrases: string[];
  filters: Record<string, string>;
  terms: string[];
}

/**
 * Enhanced Search Service with advanced features
 */
export class EnhancedSearchService {
  private static searchHistory: SearchHistory[] = [];
  private static searchSession = crypto.randomUUID();

  /**
   * Enhanced search with advanced options
   */
  static async enhancedSearch(
    query: string,
    options: EnhancedSearchOptions = {},
  ): Promise<EnhancedSearchResponse> {
    const startTime = performance.now();

    try {
      // Parse and validate query
      const parsedQuery = this.parseQuery(query);

      // Build search filters
      const filters = this.buildFilters(options);

      // Perform search with enhanced options
      const searchResponse = await SearchService.searchAll(
        parsedQuery.cleanQuery,
        {
          limit: options.limit || 20,
          types: options.types || ["blog", "course", "prompt"],
          sortBy: options.sortBy || "relevance",
        },
      );

      // Apply relevance scoring to results
      const scoredResults = await this.applyRelevanceScoring(
        searchResponse,
        parsedQuery,
        options,
      );

      // Get facets
      const facets = await this.getSearchFacets(query, options);

      // Get suggestions
      const suggestions = await this.getEnhancedSuggestions(query);

      // Get related searches
      const relatedSearches = await this.getRelatedSearches(query);

      // Calculate search time
      const searchTime = performance.now() - startTime;

      // Log analytics
      await this.logSearchAnalytics({
        query,
        resultsCount: scoredResults.total,
        searchTime,
        userAgent: navigator.userAgent,
        clickedResults: [],
        searchSession: this.searchSession,
        timestamp: new Date(),
        filters,
      });

      // Add to search history
      this.addToSearchHistory({
        query,
        timestamp: new Date(),
        resultCount: scoredResults.total,
        clickedResults: [],
        filters,
      });

      return {
        ...scoredResults,
        facets,
        suggestions,
        relatedSearches,
        searchTime,
        totalPages: Math.ceil(scoredResults.total / (options.limit || 20)),
        currentPage: 1,
      };
    } catch (error) {
      console.error("Enhanced search error:", error);
      return {
        blog: [],
        courses: [],
        prompts: [],
        total: 0,
        query,
        searchTime: 0,
        facets: {
          categories: {},
          types: {},
          authors: {},
          dateRanges: {},
          tags: {},
        },
        suggestions: [],
        relatedSearches: [],
        totalPages: 0,
        currentPage: 1,
      };
    }
  }

  /**
   * Parse search query for advanced features
   */
  private static parseQuery(query: string): ParsedQuery {
    const exactPhrases =
      query.match(/"([^"]+)"/g)?.map((p) => p.slice(1, -1)) || [];
    const filters =
      query.match(/(\w+):([^\s]+)/g)?.map((f) => {
        const [key, value] = f.split(":");
        return { key, value };
      }) || [];

    // Remove filters and exact phrases from clean query
    let cleanQuery = query;
    filters.forEach((f) => {
      cleanQuery = cleanQuery.replace(`${f.key}:${f.value}`, "");
    });
    exactPhrases.forEach((phrase) => {
      cleanQuery = cleanQuery.replace(`"${phrase}"`, "");
    });

    // Extract individual terms
    const terms = cleanQuery
      .split(/\s+/)
      .filter((term) => term.length > 0)
      .map((term) => term.toLowerCase());

    return {
      original: query,
      cleanQuery: cleanQuery.trim(),
      exactPhrases,
      filters: Object.fromEntries(filters.map((f) => [f.key, f.value])),
      terms,
    };
  }

  /**
   * Build search filters from options
   */
  private static buildFilters(options: EnhancedSearchOptions) {
    const filters: Record<string, any> = {};

    if (options.dateRange) {
      filters.dateRange = options.dateRange;
    }
    if (options.categories) {
      filters.categories = options.categories;
    }
    if (options.tags) {
      filters.tags = options.tags;
    }
    if (options.author) {
      filters.author = options.author;
    }

    return filters;
  }

  /**
   * Apply relevance scoring to search results
   */
  private static async applyRelevanceScoring(
    searchResponse: SearchResponse,
    parsedQuery: ParsedQuery,
    options: EnhancedSearchOptions,
  ): Promise<SearchResponse> {
    const scoredResults = { ...searchResponse };

    // Score blog posts
    scoredResults.blog = await Promise.all(
      searchResponse.blog.map(async (result) => {
        const score = await this.calculateRelevanceScore(
          result,
          parsedQuery,
          options,
        );
        return { ...result, score };
      }),
    );

    // Score courses
    scoredResults.courses = await Promise.all(
      searchResponse.courses.map(async (result) => {
        const score = await this.calculateRelevanceScore(
          result,
          parsedQuery,
          options,
        );
        return { ...result, score };
      }),
    );

    // Score prompts
    scoredResults.prompts = await Promise.all(
      searchResponse.prompts.map(async (result) => {
        const score = await this.calculateRelevanceScore(
          result,
          parsedQuery,
          options,
        );
        return { ...result, score };
      }),
    );

    // Sort by relevance score
    scoredResults.blog.sort((a, b) => (b.score || 0) - (a.score || 0));
    scoredResults.courses.sort((a, b) => (b.score || 0) - (a.score || 0));
    scoredResults.prompts.sort((a, b) => (b.score || 0) - (a.score || 0));

    return scoredResults;
  }

  /**
   * Calculate relevance score for a search result
   */
  private static async calculateRelevanceScore(
    result: SearchResult,
    parsedQuery: ParsedQuery,
    options: EnhancedSearchOptions,
  ): Promise<number> {
    let totalScore = 0;

    // Title match (highest weight)
    const titleScore = this.calculateTextMatch(result.title, parsedQuery) * 0.4;
    totalScore += titleScore;

    // Description match
    if (result.description) {
      const descriptionScore =
        this.calculateTextMatch(result.description, parsedQuery) * 0.3;
      totalScore += descriptionScore;
    }

    // Content match (if available)
    if (result.excerpt) {
      const contentScore =
        this.calculateTextMatch(result.excerpt, parsedQuery) * 0.2;
      totalScore += contentScore;
    }

    // Recency score
    const recencyScore =
      this.calculateRecencyScore(result.published_at || result.created_at) *
      0.05;
    totalScore += recencyScore;

    // Popularity score (placeholder - would need view/engagement data)
    const popularityScore = this.calculatePopularityScore(result) * 0.05;
    totalScore += popularityScore;

    // Exact phrase bonus
    const exactPhraseBonus = this.calculateExactPhraseBonus(
      result,
      parsedQuery.exactPhrases,
    );
    totalScore += exactPhraseBonus;

    return Math.min(100, Math.max(0, totalScore));
  }

  /**
   * Calculate text match score
   */
  private static calculateTextMatch(
    text: string,
    parsedQuery: ParsedQuery,
  ): number {
    if (!text || !parsedQuery.terms.length) return 0;

    const textLower = text.toLowerCase();
    let matchScore = 0;

    // Check for exact phrase matches
    parsedQuery.exactPhrases.forEach((phrase) => {
      if (textLower.includes(phrase.toLowerCase())) {
        matchScore += 20;
      }
    });

    // Check for individual term matches
    parsedQuery.terms.forEach((term) => {
      if (term.length > 2) {
        const regex = new RegExp(`\\b${term}\\b`, "gi");
        const matches = textLower.match(regex);
        if (matches) {
          matchScore += matches.length * 10;
        }
      }
    });

    return Math.min(100, matchScore);
  }

  /**
   * Calculate recency score
   */
  private static calculateRecencyScore(dateString?: string): number {
    if (!dateString) return 50; // Default score for items without date

    const date = new Date(dateString);
    const now = new Date();
    const daysDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff <= 7) return 100; // Very recent
    if (daysDiff <= 30) return 80; // Recent
    if (daysDiff <= 90) return 60; // Somewhat recent
    if (daysDiff <= 365) return 40; // Older
    return 20; // Very old
  }

  /**
   * Calculate popularity score
   */
  private static calculatePopularityScore(result: SearchResult): number {
    // Placeholder implementation - would need actual view/engagement data
    // For now, give higher scores to items with more metadata
    let score = 50; // Base score

    if (result.description) score += 10;
    if (result.excerpt) score += 10;
    if (result.meta_description) score += 5;

    return Math.min(100, score);
  }

  /**
   * Calculate exact phrase bonus
   */
  private static calculateExactPhraseBonus(
    result: SearchResult,
    exactPhrases: string[],
  ): number {
    if (!exactPhrases.length) return 0;

    let bonus = 0;
    const text =
      `${result.title} ${result.description || ""} ${result.excerpt || ""}`.toLowerCase();

    exactPhrases.forEach((phrase) => {
      if (text.includes(phrase.toLowerCase())) {
        bonus += 15; // Significant bonus for exact phrase matches
      }
    });

    return bonus;
  }

  /**
   * Get search facets for filtering
   */
  private static async getSearchFacets(
    query: string,
    options: EnhancedSearchOptions,
  ): Promise<SearchFacets> {
    try {
      // This would query the database to get facet counts
      // For now, return empty facets
      return {
        categories: {},
        types: {},
        authors: {},
        dateRanges: {},
        tags: {},
      };
    } catch (error) {
      console.error("Error getting search facets:", error);
      return {
        categories: {},
        types: {},
        authors: {},
        dateRanges: {},
        tags: {},
      };
    }
  }

  /**
   * Get enhanced search suggestions
   */
  private static async getEnhancedSuggestions(
    query: string,
  ): Promise<string[]> {
    try {
      // Get basic suggestions
      const basicSuggestions = await SearchService.getSearchSuggestions(query);

      // Get trending searches
      const trendingSearches = await SearchService.getTrendingSearches();

      // Get search history suggestions
      const historySuggestions = this.getSearchHistorySuggestions(query);

      // Combine and deduplicate
      const allSuggestions = [
        ...basicSuggestions,
        ...trendingSearches,
        ...historySuggestions,
      ];
      return [...new Set(allSuggestions)].slice(0, 10);
    } catch (error) {
      console.error("Error getting enhanced suggestions:", error);
      return [];
    }
  }

  /**
   * Get related searches
   */
  private static async getRelatedSearches(query: string): Promise<string[]> {
    try {
      // This would analyze search patterns to find related terms
      // For now, return empty array
      return [];
    } catch (error) {
      console.error("Error getting related searches:", error);
      return [];
    }
  }

  /**
   * Get search history suggestions
   */
  private static getSearchHistorySuggestions(query: string): string[] {
    return this.searchHistory
      .filter((h) => h.query.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 5)
      .map((h) => h.query);
  }

  /**
   * Add search to history
   */
  private static addToSearchHistory(history: Omit<SearchHistory, "timestamp">) {
    this.searchHistory.unshift({
      ...history,
      timestamp: new Date(),
    });

    // Keep only last 50 searches
    this.searchHistory = this.searchHistory.slice(0, 50);

    // Store in localStorage for persistence
    try {
      localStorage.setItem("searchHistory", JSON.stringify(this.searchHistory));
    } catch (error) {
      console.error("Error saving search history:", error);
    }
  }

  /**
   * Load search history from localStorage
   */
  private static loadSearchHistory(): void {
    try {
      const stored = localStorage.getItem("searchHistory");
      if (stored) {
        this.searchHistory = JSON.parse(stored).map((h: any) => ({
          ...h,
          timestamp: new Date(h.timestamp),
        }));
      }
    } catch (error) {
      console.error("Error loading search history:", error);
      this.searchHistory = [];
    }
  }

  /**
   * Log search analytics
   */
  private static async logSearchAnalytics(
    analytics: SearchAnalytics,
  ): Promise<void> {
    try {
      // Store in localStorage for now
      const existingAnalytics = JSON.parse(
        localStorage.getItem("searchAnalytics") || "[]",
      );
      existingAnalytics.push(analytics);
      localStorage.setItem(
        "searchAnalytics",
        JSON.stringify(existingAnalytics.slice(-100)),
      );

      // In production, this would send to analytics service
      console.log("Search analytics logged:", analytics);
    } catch (error) {
      console.error("Error logging search analytics:", error);
    }
  }

  /**
   * Get search history
   */
  static getSearchHistory(): SearchHistory[] {
    if (this.searchHistory.length === 0) {
      this.loadSearchHistory();
    }
    return [...this.searchHistory];
  }

  /**
   * Clear search history
   */
  static clearSearchHistory(): void {
    this.searchHistory = [];
    try {
      localStorage.removeItem("searchHistory");
    } catch (error) {
      console.error("Error clearing search history:", error);
    }
  }

  /**
   * Get search analytics
   */
  static getSearchAnalytics(): SearchAnalytics[] {
    try {
      return JSON.parse(localStorage.getItem("searchAnalytics") || "[]");
    } catch (error) {
      console.error("Error getting search analytics:", error);
      return [];
    }
  }

  /**
   * Get popular searches
   */
  static getPopularSearches(): { query: string; count: number }[] {
    const analytics = this.getSearchAnalytics();
    const queryCounts: { [key: string]: number } = {};

    analytics.forEach((a) => {
      queryCounts[a.query] = (queryCounts[a.query] || 0) + 1;
    });

    return Object.entries(queryCounts)
      .map(([query, count]) => ({ query, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Track clicked search result
   */
  static trackResultClick(resultId: string, query: string): void {
    // Update search history with clicked result
    const historyEntry = this.searchHistory.find((h) => h.query === query);
    if (historyEntry) {
      if (!historyEntry.clickedResults.includes(resultId)) {
        historyEntry.clickedResults.push(resultId);
      }
    }

    // Update analytics
    const analytics = this.getSearchAnalytics();
    const recentAnalytics = analytics.find((a) => a.query === query);
    if (recentAnalytics) {
      if (!recentAnalytics.clickedResults.includes(resultId)) {
        recentAnalytics.clickedResults.push(resultId);
      }
    }

    // Save updates
    try {
      localStorage.setItem("searchHistory", JSON.stringify(this.searchHistory));
      localStorage.setItem("searchAnalytics", JSON.stringify(analytics));
    } catch (error) {
      console.error("Error tracking result click:", error);
    }
  }
}

// Export convenience functions
export const enhancedSearch = EnhancedSearchService.enhancedSearch.bind(
  EnhancedSearchService,
);
export const getSearchHistory = EnhancedSearchService.getSearchHistory.bind(
  EnhancedSearchService,
);
export const clearSearchHistory = EnhancedSearchService.clearSearchHistory.bind(
  EnhancedSearchService,
);
export const getSearchAnalytics = EnhancedSearchService.getSearchAnalytics.bind(
  EnhancedSearchService,
);
export const getPopularSearches = EnhancedSearchService.getPopularSearches.bind(
  EnhancedSearchService,
);
export const trackResultClick = EnhancedSearchService.trackResultClick.bind(
  EnhancedSearchService,
);
